// @ts-nocheck
/**
 * Bottom sheets, one implementation for the whole app (styles: assets/vhp-sheet.css).
 *  - <dialog class="vhp-sheet"> with an inner .vhp-sheet__panel: opened / closed from code with a slide.
 *  - Native [popover] sheets (.app-sheet, top bar + search filters): open and light-dismiss stay declarative.
 * Both get the same drag: pull the handle / head down, or pull down while the list ([data-vhp-sheet-scroll]) is at
 * the top. Elements with [data-vhp-sheet] bind themselves; dialogs bound from code pass their own close function.
 *
 *   window.vhpSheet.setOpen(dialog, true | false)
 *   window.vhpSheet.bindDrag(sheet, onClose?)   // onClose defaults to setOpen(false) / hidePopover()
 */
(() => {
  'use strict';

  if (window.vhpSheet) return;

  const SHEET_MS = 480;
  const GRAB = '.vhp-sheet__head, .vhp-sheet__handle, .app-sheet__head, .app-sheet__handle, [data-vhp-sheet-grab]';

  const motionOk = () => !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  // Phone layout: the app shell column at any viewport, else narrow screens. Desktop dialogs are centered, no slide.
  const slides = () => document.body.classList.contains('app-shell-on') || window.matchMedia('(max-width: 959px)').matches;
  const isPopover = (sheet) => sheet.hasAttribute('popover');
  const panelOf = (sheet) => (isPopover(sheet) ? sheet : sheet.querySelector('.vhp-sheet__panel') || sheet);
  const isShown = (sheet) => {
    if (!isPopover(sheet)) return sheet.open;
    try {
      return sheet.matches(':popover-open');
    } catch (e) {
      return false;
    }
  };

  const clearClose = (sheet) => {
    if (!sheet._vhpClose) return;
    window.clearTimeout(sheet._vhpClose.timer);
    sheet._vhpClose.panel.removeEventListener('transitionend', sheet._vhpClose.onDone);
    sheet._vhpClose = null;
  };

  // Dialog sheets: slide the panel in / out, then close the dialog.
  const setOpen = (sheet, open) => {
    if (!sheet) return;
    const panel = panelOf(sheet);
    if (typeof sheet.showModal !== 'function') {
      sheet.hidden = !open;
      return;
    }
    // [hidden] is display:none !important in the UA sheet. showModal() still
    // puts the dialog in the top layer, so the page goes inert with no UI.
    sheet.removeAttribute('hidden');
    if (open) {
      clearClose(sheet);
      sheet.classList.remove('is-dragging');
      if (!sheet.open) sheet.showModal();
      if (!motionOk()) {
        panel.style.transform = '';
        sheet.classList.add('is-open');
        return;
      }
      if (sheet.classList.contains('is-open')) return;
      if (slides()) {
        // Paint the sheet fully off-screen first. Otherwise the dialog's first
        // frame is already open and the slide looks like a pop.
        panel.style.transition = 'none';
        panel.style.transform = 'translate3d(0, 100%, 0)';
        void panel.offsetWidth;
        panel.style.transition = '';
        requestAnimationFrame(() => {
          panel.style.transform = '';
          sheet.classList.add('is-open');
        });
        return;
      }
      panel.style.transform = '';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => sheet.classList.add('is-open'));
      });
      return;
    }
    if (!sheet.open) {
      sheet.classList.remove('is-open');
      return;
    }
    const finish = () => {
      clearClose(sheet);
      sheet.classList.remove('is-open', 'is-dragging');
      panel.style.transform = '';
      if (sheet.open) sheet.close();
    };
    if (!motionOk() || !sheet.classList.contains('is-open')) {
      finish();
      return;
    }
    const dragged = Boolean(panel.style.transform);
    if (!dragged) void panel.offsetWidth;
    sheet.classList.remove('is-open');
    if (dragged) {
      // Continue from where the finger let go.
      const current = panel.style.transform;
      sheet.classList.add('is-dragging');
      panel.style.transform = current;
      void panel.offsetWidth;
      sheet.classList.remove('is-dragging');
      panel.style.transform = 'translate3d(0, 110%, 0)';
    }
    const onDone = (event) => {
      if (event && (event.target !== panel || event.propertyName !== 'transform')) return;
      finish();
    };
    panel.addEventListener('transitionend', onDone);
    sheet._vhpClose = { onDone, panel, timer: window.setTimeout(finish, SHEET_MS + 80) };
  };

  // Popover sheets slide out through their own CSS transition; a drag hands over its position first.
  const hidePopover = (sheet) => {
    const panel = panelOf(sheet);
    if (panel.style.transform && motionOk()) {
      sheet.classList.remove('is-dragging');
      void panel.offsetWidth;
      panel.style.transform = 'translateY(100%)';
    }
    try {
      sheet.hidePopover();
    } catch (e) {
      panel.style.transform = '';
    }
  };

  const defaultClose = (sheet) => () => (isPopover(sheet) ? hidePopover(sheet) : setOpen(sheet, false));

  const bindDrag = (sheet, onClose) => {
    const panel = sheet && panelOf(sheet);
    if (!panel || sheet.dataset.vhpDragBound) return;
    sheet.dataset.vhpDragBound = '1';
    const close = typeof onClose === 'function' ? onClose : defaultClose(sheet);

    if (isPopover(sheet)) {
      sheet.addEventListener('toggle', (event) => {
        if (event.newState !== 'closed') return;
        sheet.classList.remove('is-dragging');
        panel.style.transform = '';
      });
    } else {
      // Escape / form close skip setOpen(): leave the next open a clean panel.
      sheet.addEventListener('close', () => {
        if (sheet.open) return;
        clearClose(sheet);
        sheet.classList.remove('is-dragging', 'is-open');
        panel.style.transform = '';
      });
    }

    let pull = null;
    let suppressClick = false;

    const apply = (dy) => {
      if (!motionOk()) return;
      panel.style.transform = `translateY(${Math.max(0, dy)}px)`;
    };

    const finish = (dy, velocity, ateClick) => {
      sheet.classList.remove('is-dragging');
      if (ateClick) {
        suppressClick = true;
        window.setTimeout(() => {
          suppressClick = false;
        }, 400);
      }
      const dismiss = dy > 80 || (dy > 24 && velocity > 0.65);
      if (!dismiss) {
        void panel.offsetWidth;
        panel.style.transform = '';
        return;
      }
      close();
    };

    const track = (state, clientY) => {
      const now = performance.now();
      const dt = now - state.lastT;
      if (dt > 0 && dt < 80) state.velocity = (clientY - state.lastY) / dt;
      state.lastY = clientY;
      state.lastT = now;
    };

    sheet.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    const startHead = (event, zone) => {
      event.preventDefault();
      try {
        zone.setPointerCapture(event.pointerId);
      } catch (e) {
        /* ignore */
      }
      const state = {
        kind: 'head',
        pointerId: event.pointerId,
        startY: event.clientY,
        lastY: event.clientY,
        lastT: performance.now(),
        velocity: 0,
      };
      pull = state;
      if (motionOk()) sheet.classList.add('is-dragging');
      const move = (ev) => {
        if (ev.pointerId !== state.pointerId) return;
        track(state, ev.clientY);
        apply(ev.clientY - state.startY);
      };
      const up = (ev) => {
        if (ev.pointerId !== state.pointerId) return;
        zone.removeEventListener('pointermove', move);
        zone.removeEventListener('pointerup', up);
        zone.removeEventListener('pointercancel', up);
        if (pull !== state) return;
        pull = null;
        finish(Math.max(0, ev.clientY - state.startY), state.velocity, true);
      };
      zone.addEventListener('pointermove', move);
      zone.addEventListener('pointerup', up);
      zone.addEventListener('pointercancel', up);
    };

    panel.addEventListener('pointerdown', (event) => {
      if (!slides() || event.button || pull || !isShown(sheet)) return;
      const zone = event.target.closest(GRAB);
      if (zone && panel.contains(zone)) {
        if (event.target.closest('button, a, input, textarea, select, label')) return;
        startHead(event, zone);
        return;
      }
      const scroller = event.target.closest('[data-vhp-sheet-scroll]');
      if (!scroller || !panel.contains(scroller) || scroller.scrollTop > 0) return;
      if (event.target.closest('input, textarea, select')) return;
      pull = {
        kind: 'scroll',
        scroller,
        pointerId: event.pointerId,
        startY: event.clientY,
        startX: event.clientX,
        lastY: event.clientY,
        lastT: performance.now(),
        velocity: 0,
        active: false,
      };
    });

    panel.addEventListener(
      'touchmove',
      (event) => {
        if (!pull || pull.kind !== 'scroll') return;
        const touch = event.touches[0];
        if (!touch) return;
        const dy = touch.clientY - pull.startY;
        const dx = touch.clientX - pull.startX;
        if (!pull.active) {
          if (pull.scroller.scrollTop > 0 || dy < 8 || Math.abs(dx) > Math.abs(dy)) {
            if (Math.abs(dx) > 8 || dy < -8 || pull.scroller.scrollTop > 0) pull = null;
            return;
          }
          pull.active = true;
          if (motionOk()) sheet.classList.add('is-dragging');
          try {
            pull.scroller.setPointerCapture(pull.pointerId);
          } catch (e) {
            /* ignore */
          }
        }
        event.preventDefault();
        track(pull, touch.clientY);
        apply(dy);
      },
      { passive: false }
    );

    const endScroll = (event) => {
      if (!pull || pull.kind !== 'scroll' || event.pointerId !== pull.pointerId) return;
      const state = pull;
      pull = null;
      if (!state.active) return;
      finish(Math.max(0, event.clientY - state.startY), state.velocity, true);
    };
    const endScrollTouch = (event) => {
      if (!pull || pull.kind !== 'scroll') return;
      const state = pull;
      pull = null;
      if (!state.active) return;
      const touch = event.changedTouches && event.changedTouches[0];
      const y = touch ? touch.clientY : state.lastY;
      finish(Math.max(0, y - state.startY), state.velocity, true);
    };
    sheet.addEventListener('pointerup', endScroll);
    sheet.addEventListener('pointercancel', endScroll);
    sheet.addEventListener('touchend', endScrollTouch);
    sheet.addEventListener('touchcancel', endScrollTouch);
  };

  window.vhpSheet = { setOpen, bindDrag, SHEET_MS };

  const bindAll = () => document.querySelectorAll('[data-vhp-sheet]').forEach((sheet) => bindDrag(sheet));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindAll, { once: true });
  else bindAll();
  // Sheets of a tab switched in-page (assets/app-router.js), e.g. its top bar's settings.
  window.addEventListener('vhp:view-show', bindAll);
})();
